import {
  IMonthNum,
  ISeasonMapping,
  ISeasonalNum,
  ISeasonalPVCharge,
  ISeasonalReduceCharge,
} from './algoInterface'

/**
 * 판매 - 절감된 계절별 기본요금, 기후환경 요금 계산
 * @param {number} defaultChargeUnit 기본요금단가
 * @param {number} envChargeUnit 기후환경요금단가
 * @param {number} beforeContractedPower 기존 계약전력
 * @param {number} afterContractedPower 변경된 계약전력
 * @param {ISeasonalNum} seasonalSum 감소된 계절별 부하 합
 * @param {ISeasonMapping} seasonMapping - 월별 계절 정보
 * @returns {ISeasonalReduceCharge} 계절별 기본요금, 기후환경 요금
 */
export const calReduceCharge = (
  defaultChargeUnit: number,
  envChargeUnit: number,
  beforeContractedPower: number,
  afterContractedPower: number,
  seasonalSum: ISeasonalNum,
  seasonMapping: ISeasonMapping
): ISeasonalReduceCharge => {
  const seasonalReduceCharge = {
    spr: {
      envCharge: 0,
      defaultCharge: 0,
    },
    sum: {
      envCharge: 0,
      defaultCharge: 0,
    },
    fall: {
      envCharge: 0,
      defaultCharge: 0,
    },
    win: {
      envCharge: 0,
      defaultCharge: 0,
    },
  }

  // 계약전력 차이
  const modifiedContractedPower = beforeContractedPower - afterContractedPower

  // 월별 감소된 기본요금
  const monthDefaultCharge = modifiedContractedPower * defaultChargeUnit

  const seasonCount = countSeasons(seasonMapping)

  seasonalReduceCharge.spr.defaultCharge = monthDefaultCharge * seasonCount.spr
  // 기후환경요금 단가에 계절별 사용량 곱
  seasonalReduceCharge.spr.envCharge = envChargeUnit * seasonalSum.spr

  seasonalReduceCharge.sum.defaultCharge = monthDefaultCharge * seasonCount.sum
  seasonalReduceCharge.sum.envCharge = envChargeUnit * seasonalSum.sum

  seasonalReduceCharge.fall.defaultCharge =
    monthDefaultCharge * seasonCount.fall
  seasonalReduceCharge.fall.envCharge = envChargeUnit * seasonalSum.fall

  seasonalReduceCharge.win.defaultCharge = monthDefaultCharge * seasonCount.win
  seasonalReduceCharge.win.envCharge = envChargeUnit * seasonalSum.win

  // 모든 항목 원단위 이하 절사
  for (let season of ['spr', 'sum', 'fall', 'win']) {
    for (let item of ['defaultCharge', 'envCharge']) {
      //@ts-ignore
      seasonalReduceCharge[season][item] = Number(
        //@ts-ignore
        seasonalReduceCharge[season][item].toFixed(0)
      )
    }
  }

  return seasonalReduceCharge
}

/**
 * 계절별 월 개수 계산
 * @param seasonMapping 월별 계절정보
 * @returns 계절별 월 개수
 */
const countSeasons = (seasonMapping: ISeasonMapping): ISeasonalNum => {
  const counts = {
    spr: 0,
    sum: 0,
    fall: 0,
    win: 0,
  }

  for (const season in seasonMapping) {
    switch (seasonMapping[season]) {
      case 'spr':
        counts.spr += 1
        break
      case 'sum':
        counts.sum += 1
        break
      case 'fall':
        counts.fall += 1
        break
      case 'win':
        counts.win += 1
        break
    }
  }

  return counts
}

/**
 * 단가 합으로 계절별 망 이용요금 / 전력거래 수수료 계산
 * @param {number} gridUsageUnitSum - 단가
 * @param {ISeasonalNum} seasonalUsage - 계절별 사용전력
 * @returns {ISeasonalNum} 계절별 이용요금
 */
export const calSeasonalExCharge = (
  gridUsageUnitSum: number,
  seasonalUsage: ISeasonalNum
): ISeasonalNum => {
  const result: ISeasonalNum = {
    spr: 0,
    sum: 0,
    fall: 0,
    win: 0,
  }
  result.spr = seasonalUsage.spr * gridUsageUnitSum
  result.sum = seasonalUsage.sum * gridUsageUnitSum
  result.fall = seasonalUsage.fall * gridUsageUnitSum
  result.win = seasonalUsage.win * gridUsageUnitSum

  // 원단위 이하 절사
  result.spr = Number(result.spr.toFixed(0))
  result.sum = Number(result.sum.toFixed(0))
  result.fall = Number(result.fall.toFixed(0))
  result.win = Number(result.win.toFixed(0))

  return result
}

/**
 * 단가로 월별 망 이용요금 / 전력거래 수수료 계산
 * @param {number} gridUsageUnitSum - 단가
 * @param {IMonthNum} seasonalUsage - 계절별 사용전력
 * @returns {IMonthNum} 계절별 이용요금
 */
export const calMonthlyExCharge = (
  gridUsageUnitSum: number,
  seasonalUsage: IMonthNum
): IMonthNum => {
  const result: IMonthNum = {
    1: 0,
    2: 0,
    3: 0,
    4: 0,
    5: 0,
    6: 0,
    7: 0,
    8: 0,
    9: 0,
    10: 0,
    11: 0,
    12: 0,
  }

  for (let index = 1; index < 13; index++) {
    //@ts-ignore
    result[index] = seasonalUsage[index] * gridUsageUnitSum
    //@ts-ignore
    result[index] = Number(result[index].toFixed(0))
  }

  return result
}

/**
 * 계산한 이익에서 수수료 빼기 /ISeasonalNum - ISeasonalNum 계산
 * @param {ISeasonalNum} prevProfit - 이익
 * @param {ISeasonalNum} tradingCharge - 수수료
 * @returns {ISeasonalNum} 계절별 순이익
 */
export const subtractPrevProfitToTradingCharge = (
  prevProfit: ISeasonalNum,
  tradingCharge: ISeasonalNum
): ISeasonalNum => {
  const result = {
    spr: 0,
    sum: 0,
    fall: 0,
    win: 0,
  }

  result.spr = Number((prevProfit.spr - tradingCharge.spr).toFixed(0))
  result.sum = Number((prevProfit.sum - tradingCharge.sum).toFixed(0))
  result.fall = Number((prevProfit.fall - tradingCharge.fall).toFixed(0))
  result.win = Number((prevProfit.win - tradingCharge.win).toFixed(0))

  return result
}

/**
 * 계산한 이익에서 수수료 빼기 /IMonthNum - IMonthNum 계산
 * @param {IMonthNum} prevProfit - 이익
 * @param {IMonthNum} tradingCharge - 수수료
 * @returns {IMonthNum} 계절별 순이익
 */
export const IMonthNumMinusIMonthNum = (
  prevProfit: IMonthNum,
  tradingCharge: IMonthNum
): IMonthNum => {
  const result: IMonthNum = {
    1: 0,
    2: 0,
    3: 0,
    4: 0,
    5: 0,
    6: 0,
    7: 0,
    8: 0,
    9: 0,
    10: 0,
    11: 0,
    12: 0,
  }

  for (let index = 1; index < 13; index++) {
    //@ts-ignore
    result[index] = Number(
      //@ts-ignore
      (prevProfit[index] - tradingCharge[index]).toFixed(0)
    )
  }

  return result
}

/**
 * IMonthNum + IMonthNum 계산
 * @param {IMonthNum}
 * @param {IMonthNum}
 * @returns {IMonthNum} 더한값
 */
export const IMonthNumPlusIMonthNum = (
  prevProfit: IMonthNum,
  tradingCharge: IMonthNum
): IMonthNum => {
  const result: IMonthNum = {
    1: 0,
    2: 0,
    3: 0,
    4: 0,
    5: 0,
    6: 0,
    7: 0,
    8: 0,
    9: 0,
    10: 0,
    11: 0,
    12: 0,
  }

  for (let index = 1; index < 13; index++) {
    //@ts-ignore
    result[index] = Number(
      //@ts-ignore
      (prevProfit[index] + tradingCharge[index]).toFixed(0)
    )
  }

  return result
}

/**
 * IMonthNum *num 계산
 * @param {IMonthNum}
 * @param {number}
 * @returns {IMonthNum} 곱한값
 */
export const IMonthNumMulNum = (
  prevProfit: IMonthNum,
  num: number
): IMonthNum => {
  const result: IMonthNum = {
    1: 0,
    2: 0,
    3: 0,
    4: 0,
    5: 0,
    6: 0,
    7: 0,
    8: 0,
    9: 0,
    10: 0,
    11: 0,
    12: 0,
  }

  for (let index = 1; index < 13; index++) {
    //@ts-ignore
    result[index] = Number(
      //@ts-ignore
      (prevProfit[index] * num).toFixed(0)
    )
  }

  return result
}

/**
 * ISeasonalNum + ISeasonalNum 계산
 * @param {ISeasonalNum} a
 * @param {ISeasonalNum} b
 * @returns {ISeasonalNum} a+b
 */
export const ISeasonalNumPlusISeasonalNum = (
  a: ISeasonalNum,
  b: ISeasonalNum
): ISeasonalNum => {
  const result = {
    spr: 0,
    sum: 0,
    fall: 0,
    win: 0,
  }

  result.spr = a.spr + b.spr
  result.sum = a.sum + b.sum
  result.fall = a.fall + b.fall
  result.win = a.win + b.win

  return result
}

/**
 * ISeasonalNum 에 숫자 곱하기
 * @param {ISeasonalNum} a
 * @param {Number} b
 * @returns {ISeasonalNum} a+b
 */
export const ISeasonalNumMulNum = (
  a: ISeasonalNum,
  b: number
): ISeasonalNum => {
  const result = {
    spr: 0,
    sum: 0,
    fall: 0,
    win: 0,
  }

  result.spr = a.spr * b
  result.sum = a.sum * b
  result.fall = a.fall * b
  result.win = a.win * b

  return result
}

/**
 * PV Charge 만 빼오기
 * @param {ISeasonalPVCharge}
 * @returns {ISeasonalNum}
 */
export const SeasonalPVChargeToSeasonalCharge = (
  seasonalPVCharge: ISeasonalPVCharge
): ISeasonalNum => {
  return {
    spr: seasonalPVCharge.spr.charge,
    sum: seasonalPVCharge.sum.charge,
    fall: seasonalPVCharge.fall.charge,
    win: seasonalPVCharge.win.charge,
  }
}

export const convertMonthNumToArray = (monthNum: IMonthNum): number[] => {
  const array: number[] = []
  for (let i = 1; i < 13; i++) {
    //@ts-ignore
    array.push(monthNum[i])
  }
  return array
}
